import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, LayoutDashboard, LogOut, Mail, MessageSquare, User as UserIcon } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";

const fmtDate = (iso, locale) => {
  try {
    return new Date(iso).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function AccountPage() {
  const { user, logout } = useAuth();
  const { locale } = useI18n();
  const isDE = locale === "de";
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/my/leads")
      .then((r) => setLeads(r.data.leads))
      .catch((err) => {
        console.warn("Failed to load inquiries", err);
        setLeads([]);
      });
  }, []);

  if (!user) return null;

  return (
    <main className="min-h-[80vh] bg-brand-ink px-6 md:px-12 lg:px-24 pt-28 pb-20" data-testid="account-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-px bg-brand-orange" />
          <span className="label-mono">{isDE ? "Kundenbereich" : "Client area"}</span>
        </div>

        {/* Profile card */}
        <div className="glass-strong rounded-3xl p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-white/15" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-brand-orange/15 border border-brand-orange/40 flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-brand-orange" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="font-display font-bold text-2xl text-white tracking-tight" data-testid="account-name">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-white/60">
              <Mail className="w-3.5 h-3.5" /> <span data-testid="account-email">{user.email}</span>
              {user.role === "admin" && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-brand-orange/15 border border-brand-orange/40 text-brand-orange text-[10px] font-bold uppercase tracking-wider">Admin</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user.role === "admin" && (
              <Link to="/admin" data-testid="account-admin-link" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-orange text-white text-xs font-semibold hover:bg-brand-orange/90 transition-all">
                <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
              </Link>
            )}
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass border border-white/15 text-white/75 text-xs font-medium hover:border-brand-orange hover:text-brand-orange transition-all">
              <LogOut className="w-3.5 h-3.5" /> {isDE ? "Abmelden" : "Sign out"}
            </button>
          </div>
        </div>

        {/* Inquiries */}
        <h2 className="font-display font-bold text-xl text-white tracking-tight mt-10 mb-4">
          {isDE ? "Deine Anfragen" : "Your inquiries"}
        </h2>
        {leads === null ? (
          <div className="text-white/50 text-sm font-mono">{isDE ? "Lädt…" : "Loading…"}</div>
        ) : leads.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center" data-testid="no-inquiries">
            <MessageSquare className="w-6 h-6 text-brand-orange mx-auto mb-3" />
            <p className="text-white/65 text-sm">
              {isDE
                ? "Noch keine Anfragen mit dieser E-Mail. Starte über das Kontaktformular oder den Chat."
                : "No inquiries linked to this email yet. Start via the contact form or the chat."}
            </p>
            <Link to="/contact" className="inline-block mt-4 text-brand-orange text-sm font-semibold hover:underline">
              {isDE ? "Anfrage senden →" : "Send an inquiry →"}
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="inquiries-list">
            {leads.map((l) => (
              <div key={l.id} className="glass rounded-2xl p-5">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-brand-orange/12 border border-brand-orange/35 text-brand-orange font-semibold">{l.service || "General"}</span>
                  {l.budget && <span className="px-2.5 py-1 rounded-full glass text-white/70">{l.budget}</span>}
                  {l.timeline && <span className="px-2.5 py-1 rounded-full glass text-white/70">{l.timeline}</span>}
                  <span className="ml-auto font-mono text-white/40">{fmtDate(l.created_at, locale)}</span>
                </div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed whitespace-pre-line line-clamp-4">{l.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Book CTA */}
        <div className="glass-strong rounded-3xl p-6 md:p-8 mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold text-white">{isDE ? "Nächster Schritt?" : "Next step?"}</div>
            <p className="text-sm text-white/60 mt-1">
              {isDE ? "Buche ein kostenloses 30-Minuten-Strategiegespräch." : "Book a free 30-minute strategy call."}
            </p>
          </div>
          <Link to="/book" data-testid="account-book-cta" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange/90 transition-all shrink-0">
            <Calendar className="w-4 h-4" /> {isDE ? "Termin buchen" : "Book a call"}
          </Link>
        </div>
      </div>
    </main>
  );
}
